import { db } from "../firebase";
import {
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  collectionGroup,
  collection,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import {
  ASSOCIATESTATUS_TYPES,
  PUSH_NOTIFICATION_TYPES,
  WORKFLOW_SCHEDULER_TYPES,
  WORKFLOW_FEED_TYPES,
  FB_COLLECTION_PATH,
} from "./postActionTypes";
import { fx_FB_SendNotification } from "./Fx_SendPushNotification";
import { chatFunction } from "./Fx_Chats";

// ================================== ASSOCIATE FUNCTIONS ==================================

export async function fx_FB_SendAssociateRequest(
  requestorUID: string,
  requestorFName: string,
  requestorLName: string,
  requestorCompany: string,
  requestorProfilePicURL: string,
  targetUID: string,
  targetFName: string,
  targetLName: string,
  targetCompany: string,
  targetProfilePicURL: string
) {
  // ====================== set's the userAssociations records for self and target (begin) =============
  // updates current user's associate request
  const userRef = doc(db, FB_COLLECTION_PATH.UserAssociations, requestorUID);
  await setDoc(
    userRef,
    {
      [targetUID]: {
        firstName: targetFName,
        lastName: targetLName,
        company: targetCompany,
        profilePicURL: targetProfilePicURL,
        associateStatus: ASSOCIATESTATUS_TYPES.RequestSent,
        isRead: true,
        uid: targetUID,
      },
    },
    { merge: true }
  );
  // updates targeted user
  const targetRef = doc(db, FB_COLLECTION_PATH.UserAssociations, targetUID);
  await setDoc(
    targetRef,
    {
      [requestorUID]: {
        firstName: requestorFName,
        lastName: requestorLName,
        company: requestorCompany,
        profilePicURL: requestorProfilePicURL,
        associateStatus: ASSOCIATESTATUS_TYPES.RequestReceived,
        isRead: false,
        uid: requestorUID,
      },
    },
    { merge: true }
  );
  // Send notification to the recipient
  fx_FB_SendNotification(
    requestorUID,
    [targetUID],
    PUSH_NOTIFICATION_TYPES.AssociationRequest,
    {
      fromFName: requestorFName,
      fromUID: requestorUID,
    }
  );
}

export async function fx_FB_CancelAssociateRequest(
  requestorUID: string,
  requestorFName: string,
  requestorLName: string,
  requestorCompany: string,
  requestorProfilePicURL: string,
  targetUID: string,
  targetFName: string,
  targetLName: string,
  targetCompany: string,
  targetProfilePicURL: string
) {
  const userRef = doc(db, FB_COLLECTION_PATH.UserAssociations, requestorUID);
  await setDoc(
    userRef,
    {
      [targetUID]: {
        firstName: targetFName,
        lastName: targetLName,
        company: targetCompany,
        profilePicURL: targetProfilePicURL,
        associateStatus: ASSOCIATESTATUS_TYPES.Cancelled,
        isRead: false,
        uid: targetUID,
      },
    },
    { merge: true }
  );
  // updates targeted user
  const targetRef = doc(db, FB_COLLECTION_PATH.UserAssociations, targetUID);
  await setDoc(
    targetRef,
    {
      [requestorUID]: {
        firstName: requestorFName,
        lastName: requestorLName,
        company: requestorCompany,
        profilePicURL: requestorProfilePicURL,
        associateStatus: ASSOCIATESTATUS_TYPES.Cancelled,
        isRead: false,
        uid: requestorUID,
      },
    },
    { merge: true }
  );
  return { status: WORKFLOW_FEED_TYPES.Cancelled };
}

export async function fx_FB_AcceptAssociateRequest(
  requestorUID: string,
  requestorFName: string,
  requestorLName: string,
  requestorCompany: string,
  requestorProfilePicURL: string,
  targetUID: string,
  targetFName: string,
  targetLName: string,
  targetCompany: string,
  targetProfilePicURL: string
): Promise<string | undefined> {
  // updates current user's associate request
  const userRef = doc(db, FB_COLLECTION_PATH.UserAssociations, requestorUID);
  await updateDoc(userRef, {
    [targetUID]: {
      firstName: targetFName,
      lastName: targetLName,
      company: targetCompany,
      profilePicURL: targetProfilePicURL,
      associateStatus: ASSOCIATESTATUS_TYPES.Associate,
      uid: targetUID,
    },
  });
  // updates targeted user
  const targetRef = doc(db, FB_COLLECTION_PATH.UserAssociations, targetUID);
  await updateDoc(targetRef, {
    [requestorUID]: {
      firstName: requestorFName,
      lastName: requestorLName,
      company: requestorCompany,
      profilePicURL: requestorProfilePicURL,
      associateStatus: ASSOCIATESTATUS_TYPES.Associate,
      uid: requestorUID,
    },
  });
  const userRef2 = doc(db, FB_COLLECTION_PATH.Users, requestorUID);
  await updateDoc(userRef2, {
    associates_Associates: arrayUnion(targetUID),
  });
  const targetRef2 = doc(db, FB_COLLECTION_PATH.Users, targetUID);
  await updateDoc(targetRef2, {
    associates_Associates: arrayUnion(requestorUID),
  });

  const chatId = await chatFunction(
    {
      UserId: requestorUID,
      FirstName: requestorFName,
      LastName: requestorLName,
      ProfilePicURL: requestorProfilePicURL,
    },
    {
      WerkerUid: targetUID,
      WerkerFirstName: targetFName,
      WerkerLastName: targetLName,
      WerkerProfilePicURL: targetProfilePicURL,
    },
    "DM"
  );

  return chatId;
}

export async function fx_FB_DeclineAssociateRequest(
  requestorUID: string,
  requestorFName: string,
  requestorLName: string,
  requestorCompany: string,
  requestorProfilePicURL: string,
  targetUID: string,
  targetFName: string,
  targetLName: string,
  targetCompany: string,
  targetProfilePicURL: string
) {
  // updates current user's associate request
  const userRef = doc(db, FB_COLLECTION_PATH.UserAssociations, requestorUID);
  await updateDoc(userRef, {
    [targetUID]: {
      firstName: targetFName,
      lastName: targetLName,
      company: targetCompany,
      profilePicURL: targetProfilePicURL,
      associateStatus: ASSOCIATESTATUS_TYPES.Declined,
      uid: targetUID,
    },
  });
  // updates targeted user
  const targetRef = doc(db, FB_COLLECTION_PATH.UserAssociations, targetUID);
  await updateDoc(targetRef, {
    [requestorUID]: {
      firstName: requestorFName,
      lastName: requestorLName,
      company: requestorCompany,
      profilePicURL: requestorProfilePicURL,
      associateStatus: ASSOCIATESTATUS_TYPES.Declined,
      uid: requestorUID,
    },
  });
}

async function removeFiredAssociateFromCrews(
  requestorUID: string,
  firedUID: string
): Promise<void> {
  const crewsRef = collection(db, FB_COLLECTION_PATH.Crews);

  // Keep it simple: query by SchedulerId only (avoids composite index needs)
  const q = query(crewsRef, where("SchedulerId", "==", requestorUID));
  const snap = await getDocs(q);

  const batch = writeBatch(db);
  let touched = 0;

  snap.forEach((d) => {
    const data: any = d.data() || {};
    const memberIds = Array.isArray(data.CrewMemberIds)
      ? (data.CrewMemberIds as string[])
      : [];

    if (!memberIds.includes(firedUID)) return;

    const ref = doc(db, FB_COLLECTION_PATH.Crews, d.id);
    batch.update(ref, {
      CrewMemberIds: arrayRemove(firedUID),
      UpdatedAt: serverTimestamp(),
    });

    touched += 1;
  });

  if (touched > 0) {
    await batch.commit();
  }
}

export async function fx_FB_FireAssociate(
  requestorUID: string,
  requestorFName: string,
  requestorLName: string,
  requestorCompany: string,
  requestorProfilePicURL: string,
  targetUID: string,
  targetFName: string,
  targetLName: string,
  targetCompany: string,
  targetProfilePicURL: string
) {
  // 1 - Update the userAssociates for requester / target
  // updates current user's associate request
  const userRef = doc(db, FB_COLLECTION_PATH.UserAssociations, requestorUID);
  await updateDoc(userRef, {
    [targetUID]: {
      firstName: targetFName,
      lastName: targetLName,
      company: targetCompany,
      profilePicURL: targetProfilePicURL,
      associateStatus: ASSOCIATESTATUS_TYPES.Fired,
      uid: targetUID,
    },
  });
  // updates targeted user
  const targetRef = doc(db, FB_COLLECTION_PATH.UserAssociations, targetUID);
  await updateDoc(targetRef, {
    [requestorUID]: {
      firstName: requestorFName,
      lastName: requestorLName,
      company: requestorCompany,
      profilePicURL: requestorProfilePicURL,
      associateStatus: ASSOCIATESTATUS_TYPES.Fired,
      uid: requestorUID,
    },
  });

  const userRef2 = doc(db, FB_COLLECTION_PATH.Users, requestorUID);
  await updateDoc(userRef2, {
    associates_Associates: arrayRemove(targetUID),
  });
  const targetRef2 = doc(db, FB_COLLECTION_PATH.Users, targetUID);
  await updateDoc(targetRef2, {
    associates_Associates: arrayRemove(requestorUID),
  });

  // 2 - Identify and remove all instances of the target from the requestors Open / Filled Jobs (invited list)
  // query to get all the jobs
  // console.log("looking for fired werker in Open / Filled jobs");
  const jobsToRemoveWerker = query(
    collectionGroup(db, "jobStatus"),
    where("docStamp.userInfoFrom.uid", "==", requestorUID),
    where("jobInfo.status.jobStatusSched", "in", [
      WORKFLOW_SCHEDULER_TYPES.Open,
      WORKFLOW_SCHEDULER_TYPES.Filled,
    ]),
    where("jobInfo.werkersInfo.invitedWerkers", "array-contains", targetUID)
  );
  const querySnapshot = await getDocs(jobsToRemoveWerker);
  // loop thru and remove the target's uid
  querySnapshot.forEach((doc: any) => {
    // console.log(doc.data());
    var jobDocument = doc.data();
    // console.log(jobDocument.jobInfo.werkersInfo.invitedWerkers);
    // console.log(jobDocument.docStamp.docInfo.docId);
    var jobIdentifier: string = jobDocument.docStamp.docInfo.parent.docId;
    removeTheFiredAssociate(jobIdentifier, requestorUID, targetUID);
  });
  await removeFiredAssociateFromCrews(requestorUID, targetUID);
}
function removeTheFiredAssociate(
  jobId: string,
  schedUID: string,
  firedUser: string
) {
  const jobTargetToRemove = doc(
    db,
    "users/" + schedUID + "/jobs/" + jobId + "/jobStatus",
    jobId + "_Status"
  );
  setDoc(
    jobTargetToRemove,
    {
      jobInfo: { werkersInfo: { invitedWerkers: arrayRemove(firedUser) } },
    },
    { merge: true }
  );
}

export async function fx_FB_MarkAssociateRequestRead(
  profileUid: string,
  associatesUid: string
) {
  const jobRef = doc(db, FB_COLLECTION_PATH.UserAssociations, profileUid);
  await setDoc(jobRef, { [associatesUid]: { isRead: true } }, { merge: true });
}
